import _ from 'lodash'
import { getDisplayedId, getFullId, getFullItemId } from '@wix/thunderbolt-commons'
import { composeSDKFactories, createElementPropsSDKFactory, createAccessibilityPropSDKFactory } from '@wix/editor-elements-corvid-utils/dist/esm'
import type { IModelsAPI, StoresSdkHandlers, $W } from '@wix/thunderbolt-symbols'
import type { componentSdkFactoryArgs, ComponentSdkFactory } from '@wix/thunderbolt-platform-types'
import type { ColumnsRepeaterSDK, DataItem, OnItemCallback, Reporter, OnItemRemovedCallback } from '../../../types'
import type { IWixSelector } from '../../modules/wixSelector'
import type { IInstanceCache } from '../../modules/instanceCache'
import type { IComponentSdkState } from '../../modules/componentSdkState'
import type { IControllerEvents } from '../../modules/controllerEvents'
import { getScopedInstancesForRole } from '../../repeaterUtils'
import { createBatchPromisesManager } from './batchedUpdatesManager'
import { name as repeaters, RepeatersSdkHandlers } from 'feature-repeaters'
import type { CreateRepeatedControllersFn } from '../../modules/applications'

type RepeaterState = {
	onItemReadyCallbacks: Array<Function>
	onItemRemovedCallbacks: Array<Function>
	currentItems: Array<any>
	isItemsSet: boolean
	handleAddedItemsPromise: Promise<void>
	onItemSelectedCallbacks: Array<Function>
}

interface UpdateItems {
	items: Array<string>
	removedItems: Array<string>
	didItemSet: boolean
}

const inputTypes = {
	radio: 'radio',
	checkbox: 'checkbox',
}
const repeaterRoles = {
	group: 'group',
	radiogroup: 'radiogroup',
}

const getInitialRepeaterState = (initialItems: Array<{ _id: string }> = []): RepeaterState => {
	return {
		onItemReadyCallbacks: [],
		onItemRemovedCallbacks: [],
		isItemsSet: false,
		currentItems: initialItems,
		handleAddedItemsPromise: Promise.resolve(),
		onItemSelectedCallbacks: [],
	}
}

function createRepeatersState() {
	const repeaterStateByRepeaterId: Record<string, RepeaterState> = {}
	return {
		getOrCreateRepeaterState(repeaterId: string, initialItems: Array<{ _id: string }>): RepeaterState {
			if (!repeaterStateByRepeaterId[repeaterId]) {
				repeaterStateByRepeaterId[repeaterId] = getInitialRepeaterState(initialItems)
			}

			return repeaterStateByRepeaterId[repeaterId]
		},
	}
}

export function RepeaterSdk({
	modelsApi,
	wixSelector,
	reporter,
	sdkInstancesCache,
	componentSdkState,
	createRepeatedControllers,
	handlers: { stores: storesHandlers },
	controllerEvents,
}: {
	modelsApi: IModelsAPI
	wixSelector: IWixSelector
	reporter: Reporter
	sdkInstancesCache: IInstanceCache
	componentSdkState: IComponentSdkState
	createRepeatedControllers: CreateRepeatedControllersFn
	handlers: StoresSdkHandlers
	controllerEvents: IControllerEvents
}): ComponentSdkFactory {
	const { getOrCreateRepeaterState } = createRepeatersState()

	const createScoped$wFactory = (repeaterId: string, controllerCompId: string) => (itemId: string) => {
		const $wScope = wixSelector.create$wRepeaterScope({ compId: repeaterId, itemId })
		const getInstanceForRole = getScopedInstancesForRole({
			modelsApi,
			controllerCompId,
			repeaterId,
			itemId,
			getInstanceFn: wixSelector.getInstance,
			$wScope,
			isRepeatedSlots: false,
		})
		return wixSelector.$wFactory({ controllerId: controllerCompId, getInstancesForRole: getInstanceForRole, repeaterId })
	}

	const getIdsFromItems = (items: Array<DataItem>) => items.map((item) => item._id)

	const getAddedItems = (currentData: Array<DataItem>, newData: Array<DataItem>) => {
		const currentDataIds = getIdsFromItems(currentData)
		return newData.filter((newItem) => !currentDataIds.includes(newItem._id))
	}
	const getRemovedItems = (currentData: Array<DataItem>, newData: Array<DataItem>) => {
		const newDataIds = getIdsFromItems(newData)
		return currentData.filter((currentItem) => !newDataIds.includes(currentItem._id))
	}

	const reportWrongType = (propertyName: string, functionName: string, wrongValue: any, expectedType: string) => {
		reporter.logSdkError(`The ${propertyName} parameter that is passed to the ${functionName} method cannot be set to the value ${wrongValue}. It must be of type ${expectedType}.`)
	}

	const isString = (val: any) => typeof val === 'string' || val instanceof String

	const isFunction = (maybeFunction: any): maybeFunction is Function => {
		return typeof maybeFunction === 'function'
	}

	const repeaterItemIdRegex = /^[a-zA-Z\d-]+$/
	const validateIdFormat = (id: string) => repeaterItemIdRegex.test(id)
	const validateDataArrayAndReport = (data: Array<DataItem>, repeaterId: string): boolean => {
		if (!Array.isArray(data)) {
			reportWrongType('data', 'data', data, 'array')
			return false
		}
		const ids: { [id: string]: boolean } = {}
		return data.every((item, index) => {
			if (!item._id) {
				reporter.logSdkError('Each item in the items array must have a member named `_id` which contains a unique value identifying the item.')
				return false
			}
			if (!isString(item._id)) {
				reportWrongType('_id', 'data', item._id, 'string')
				return false
			}
			if (!validateIdFormat(item._id)) {
				reporter.logSdkError(
					`Error setting the data property of ${repeaterId}. The data array contains the invalid ID ${item._id} at index ${index}. IDs must be comprised of only the following characters: A-Z, a-z, 0-9, and dashes.`
				)
				return false
			}
			if (ids[item._id]) {
				reporter.logSdkWarning(`The data that was passed to data contained at least two items with the same ID: ${item._id}. Only the first item was accepted.`)
			} else {
				ids[item._id] = true
			}
			return true
		})
	}

	const logErrorFromFunction = (compId: string, functionName: string, errorMessage: string) => {
		reporter.logSdkError(`The ${functionName} function of ${compId} threw the following error: ${errorMessage}`)
	}

	function updateAddedItemsStyles(repeaterChildComponents: Array<string>, itemIds: Array<string>) {
		repeaterChildComponents.forEach((templateId) => {
			itemIds.forEach((itemId) => {
				// we want the style overrides store to contain an entry for the new item
				// so that if there are style overrides on the template, they will apply on the newly added item.
				void storesHandlers.updateStyles({ [getDisplayedId(templateId, itemId)]: {} })
			})
		})
	}

	const getFirstRepeaterChild = (repeaterId: string) => {
		const [firstChild] = modelsApi.getContainerChildrenIds(repeaterId)
		return firstChild
	}

	const isSelectableRepeater = (repeaterChild: string): boolean => {
		if (!repeaterChild) {
			return false
		}

		const childType = modelsApi.getCompType(repeaterChild)
		return childType?.includes('SelectableContainer') || false
	}

	const sdk = ({
		props,
		setProps,
		compId: repeaterId,
		controllerCompId,
		sdkData,
		handlers,
	}: componentSdkFactoryArgs<any, { repeaterChildComponents: Array<string> }, any, RepeatersSdkHandlers>): ColumnsRepeaterSDK => {
		const repeaterTemplateId = getFullId(repeaterId)
		const repeaterChild = getFirstRepeaterChild(repeaterId)
		const repeaterItemId = getFullItemId(repeaterId)
		const initialItems = props.items.map((id: string) => ({ _id: getFullId(id) }))
		const selectableRepeater = isSelectableRepeater(repeaterChild)
		const repeaterState = getOrCreateRepeaterState(repeaterId, initialItems)
		const shouldRunTemplateRepeaterCallbacks = repeaterId !== repeaterTemplateId
		const templateRepeaterState = shouldRunTemplateRepeaterCallbacks ? getOrCreateRepeaterState(repeaterTemplateId, initialItems) : getInitialRepeaterState()
		const createScoped$w = createScoped$wFactory(repeaterTemplateId, controllerCompId)

		const getDisplayedIdsFromItems = (items: Array<DataItem>) => items.map((item) => (repeaterItemId ? getDisplayedId(item._id, repeaterItemId) : item._id))

		const handleAddedItems = async (newData: Array<DataItem>, addedItems: Array<DataItem>): Promise<any> => {
			if (!addedItems.length) {
				return Promise.resolve()
			}

			const addedItemsFullIds = getDisplayedIdsFromItems(addedItems)
			handlers[repeaters].handleAddedItems(addedItemsFullIds, repeaterTemplateId)
			const onItemReadyCallbacks = [...templateRepeaterState.onItemReadyCallbacks, ...repeaterState.onItemReadyCallbacks]
			updateAddedItemsStyles(sdkData.repeaterChildComponents, addedItemsFullIds)

			// synchronously invoke createRepeatedControllers so it invokes on the same task as "set data()"
			const runControllersOnReadys = await createRepeatedControllers(repeaterTemplateId, addedItemsFullIds)
			// run onItemReadyCallbacks before runControllersOnReadys to allow setting the initial state of repeated controllers
			const addedItemsIds = getIdsFromItems(addedItems)
			newData.forEach((item, index) => {
				if (addedItemsIds.includes(item._id)) {
					onItemReadyCallbacks.forEach((cb) => cb(item, index, repeaterItemId))
				}
			})

			return runControllersOnReadys()
		}

		const handleRemovedItems = (removedItems: Array<DataItem>, prevRepeaterItems: Array<DataItem>) => {
			if (!removedItems.length) {
				return
			}

			const removedItemsFullIds = getDisplayedIdsFromItems(removedItems)
			handlers[repeaters].handleRemovedItems(removedItemsFullIds, repeaterTemplateId)

			if (repeaterState.isItemsSet) {
				const removedItemsIds = getIdsFromItems(removedItems)

				prevRepeaterItems.forEach((item) => {
					if (removedItemsIds.includes(item._id)) {
						const onItemRemovedCallbacks = [...templateRepeaterState.onItemRemovedCallbacks, ...repeaterState.onItemRemovedCallbacks]
						onItemRemovedCallbacks.forEach((cb) => cb(item))
					}
				})

				sdkInstancesCache.clearCacheByPredicate(({ itemId }) => removedItemsFullIds.includes(itemId!))
				componentSdkState.clearStateByPredicate((compId) => removedItemsFullIds.includes(getFullItemId(compId)))
			}

			removedItemsFullIds.forEach((itemId) => {
				sdkData.repeaterChildComponents.forEach((compId) => {
					const displayedId = getDisplayedId(compId, itemId)
					modelsApi.clearProps(displayedId)
				})
				controllerEvents.disposeRepeatedControllerEvents(repeaterId, itemId)
			})
		}

		const updateRepeaterItems = (updateItemsArr: Array<UpdateItems>) => {
			const isRepeaterTemplate = modelsApi.isRepeaterTemplate(repeaterId)
			const repeaterDisplayIds: Array<string> = []
			const newItems = updateItemsArr[updateItemsArr.length - 1]?.items
			// didItemSet is false only on the first set data. The first set data is the one that changes the static repeater items to have a dynamic items.
			// If the repeater would be disconnected, the static items should be re-displayed. Therefore the static items shell never be removed.
			const removedItemIds = _.flatten(updateItemsArr.map((updateItems) => (updateItems.didItemSet ? updateItems.removedItems : []))).filter((item) => !newItems || !newItems.includes(item))
			// This method is called after a batch, and only the last set data with the last items should be displayed in the repeater.

			// The reason we dont use the setProps provided for the SDK, is that we want to
			// update only plafrom modelsApi props. handlers[handleRepeaterDataUpdate] will update viewer props store.
			modelsApi.updateProps(repeaterId, {
				items: newItems,
			})
			if (isRepeaterTemplate) {
				modelsApi.getDisplayedIdsOfRepeaterTemplate(repeaterId).forEach((displayedId: string) => {
					modelsApi.updateProps(displayedId, {
						items: newItems,
					})
					repeaterDisplayIds.push(displayedId)
				})
			}
			handlers[repeaters].handleRepeaterDataUpdate(removedItemIds, newItems, repeaterId, { isRepeaterTemplate, repeaterDisplayIds })
		}

		const callItemCallback = (cb: OnItemCallback) => (item: DataItem, index: number, _repeaterItemId?: string) => {
			return cb(createScoped$w(repeaterItemId || _repeaterItemId ? getDisplayedId(item._id, _repeaterItemId || repeaterItemId) : item._id), item, index)
		}

		const callCallbackWithLog = <F extends (...args: Array<any>) => any>(functionName: string, cb: F) => (...args: Parameters<F>) => {
			try {
				cb(...args)
			} catch (ex) {
				logErrorFromFunction(repeaterTemplateId, functionName, ex.message)
			}
		}

		const validateFunctionTypeAndReport = (maybeFunction: any, propertyName: string, functionName: string): maybeFunction is Function => {
			if (!isFunction(maybeFunction)) {
				reportWrongType(propertyName, functionName, maybeFunction, 'function')
				return false
			}
			return true
		}

		const validateSelectableRepeaterAndReport = (functionName: string) => {
			if (!selectableRepeater) {
				reporter.logSdkError(`${functionName} can only be used with a selectable repeater.`)
			}

			return selectableRepeater
		}
		const reportInvalidSelectableRepeaterType = (functionName: string) => {
			reporter.logSdkError(`${functionName} can only be used with a ${!getMultiselectable() ? 'multi-selectable repeater' : 'selectable repeater'}.`)
		}

		const getSelectableContainerVelo = ($item: $W) => {
			const selectableContainerCompId = modelsApi.getRoleForCompId(repeaterChild, controllerCompId) as string
			return $item(`#${selectableContainerCompId}`)
		}

		const getCurrentIndices = () => {
			const currentIndices: Array<number> = []
			repeaterState.currentItems.forEach((item: DataItem, index: number) =>
				callItemCallback(($item) => {
					const selectableContainer = getSelectableContainerVelo($item)

					if (selectableContainer.selected) {
						currentIndices.push(index)
					}
				})(item, index)
			)
			return currentIndices
		}

		const getMultiselectable = () => {
			return props.multiselectable || false
		}

		const getInputType = () => {
			return getMultiselectable() ? inputTypes.checkbox : inputTypes.radio
		}

		const getAriaAttributes = (multiselectable = props.multiselectable) => ({
			...props.ariaAttributes,
			role: multiselectable ? repeaterRoles.group : repeaterRoles.radiogroup,
		})

		const setSelectedIndices = (indices: Array<number>) => {
			repeaterState.currentItems.forEach((item: DataItem, index: number) =>
				callItemCallback(($item) => {
					const selectableContainer = getSelectableContainerVelo($item)
					selectableContainer.selected = indices.includes(index)
				})(item, index)
			)
		}

		const updateSelectedIndices = (indexToUpdate: number) => {
			if (getMultiselectable()) {
				const currentIndices: Array<number> = getCurrentIndices()

				const shouldToggleIndex = currentIndices.includes(indexToUpdate)
				const newIndices = shouldToggleIndex ? currentIndices.filter((item) => item !== indexToUpdate) : [...currentIndices, indexToUpdate]

				setSelectedIndices(newIndices)
			} else {
				setSelectedIndices([indexToUpdate])
			}
		}

		const batchedUpdatesManager = createBatchPromisesManager(updateRepeaterItems)

		const setItemReadyCallbacks = (cb: OnItemCallback, logName: string) => {
			const onItemReadyCallback = callItemCallback(cb)
			repeaterState.onItemReadyCallbacks.push(callCallbackWithLog(logName, onItemReadyCallback))

			if (repeaterState.isItemsSet) {
				repeaterState.handleAddedItemsPromise.then(() => {
					repeaterState.currentItems.forEach((item: DataItem, index: number) => onItemReadyCallback(item, index))
				})
			}
		}

		if (selectableRepeater) {
			setItemReadyCallbacks(($item, item, index) => {
				const selectableContainer = getSelectableContainerVelo($item)
				if (!selectableContainer || !selectableContainer.onSelect) {
					reporter.logSdkError('selectableContainer is missing in the repeater item')
					return
				}

				selectableContainer.inputType = getInputType()
				selectableContainer.onSelect(() => {
					updateSelectedIndices(index)
					const onItemSelectedCallbacks = [...templateRepeaterState.onItemSelectedCallbacks, ...repeaterState.onItemSelectedCallbacks]
					onItemSelectedCallbacks.forEach((cb) => cb(item, index))
				})
			}, 'onItemReadySelectCallback')
			setProps({
				ariaAttributes: getAriaAttributes(),
			})
		}

		return {
			get data() {
				// cloning currentItems because if user get currentItems he can mutate them and then break our getAddedItems functionality
				return _.clone(repeaterState.currentItems)
			},
			set data(newData) {
				if (!validateDataArrayAndReport(newData, repeaterTemplateId)) {
					return
				}

				const prevRepeaterItems = repeaterState.currentItems
				const uniqueNewData = _.uniqBy(newData, '_id')

				const itemIds = getIdsFromItems(uniqueNewData)
				Object.assign(props, { items: itemIds })
				const didItemSet = repeaterState.isItemsSet
				// running handleRemovedItems before updating the currentItems so any use of the currentItems inside onItemRemove will not work
				const removedItems = getRemovedItems(prevRepeaterItems, uniqueNewData)
				handleRemovedItems(removedItems, prevRepeaterItems)
				// setting currentItems after calculating addedItemsIds to not override reference of prevRepeaterItems
				repeaterState.currentItems = uniqueNewData
				repeaterState.isItemsSet = true
				// handling addedItems after updating currentItems so that forEach api will work with the new items
				const addedItems = getAddedItems(prevRepeaterItems, uniqueNewData)
				const handleAddedItemsPromise = handleAddedItems(uniqueNewData, addedItems)
				repeaterState.handleAddedItemsPromise = handleAddedItemsPromise

				const removedItemsIds = getIdsFromItems(removedItems)

				const updateItems: UpdateItems = { items: itemIds, removedItems: removedItemsIds, didItemSet }

				// Set data can be called multiple times in one task, and there might be a change in the order of the resolve of the handleAddedItemsPromise.
				// Hence the following promise is batched until the next task.
				// Then, in the next task await for all promises to resolve while maintaining the resolved promises values order.
				batchedUpdatesManager.batch(handleAddedItemsPromise.then(() => updateItems))
			},
			get selectedIndex() {
				if (!validateSelectableRepeaterAndReport('selectedIndex')) {
					return undefined
				}

				if (getMultiselectable()) {
					reportInvalidSelectableRepeaterType('selectedIndex')
					return undefined
				}

				return getCurrentIndices()[0]
			},
			set selectedIndex(index) {
				if (!validateSelectableRepeaterAndReport('selectedIndex')) {
					return
				}

				if (getMultiselectable()) {
					reportInvalidSelectableRepeaterType('selectedIndex')
					return
				}

				if (!_.isNumber(index)) {
					reportWrongType('index', 'selectedIndex', index, 'number')
					return
				}

				if (!_.isNumber(index) || index < 0 || index >= repeaterState.currentItems.length) {
					reporter.logSdkError(`selectedIndex must be a number between 0 and ${repeaterState.currentItems.length - 1}.`)
					return
				}

				setSelectedIndices([index])
			},
			get selectedIndices() {
				if (!validateSelectableRepeaterAndReport('selectedIndices')) {
					return undefined
				}
				if (!getMultiselectable()) {
					reportInvalidSelectableRepeaterType('selectedIndices')
					return undefined
				}

				return getCurrentIndices().sort((a: number, b: number) => a - b)
			},
			set selectedIndices(indices) {
				if (!validateSelectableRepeaterAndReport('selectedIndices')) {
					return
				}
				if (!getMultiselectable()) {
					reportInvalidSelectableRepeaterType('selectedIndices')
					return
				}

				if (!_.isArray(indices)) {
					reportWrongType('indices', 'selectedIndices', indices, 'number array')
					return
				}
				if (!indices.every((index) => _.isNumber(index) && index <= repeaterState.currentItems.length)) {
					reporter.logSdkError(`selectedIndices must contain numbers between 0 and ${repeaterState.currentItems.length - 1}.`)
					return
				}

				setSelectedIndices([...new Set(indices)])
			},
			get multiselectable() {
				if (!validateSelectableRepeaterAndReport('multiselectable')) {
					return undefined
				}

				return getMultiselectable()
			},
			set multiselectable(multiselectable) {
				if (!validateSelectableRepeaterAndReport('multiselectable')) {
					return
				}

				if (!_.isBoolean(multiselectable)) {
					reportWrongType('multiselectable', 'multiselectable', multiselectable, 'boolean')
					return
				}

				setSelectedIndices([])
				setProps({
					multiselectable,
					ariaAttributes: getAriaAttributes(multiselectable),
				})
				repeaterState.currentItems.forEach((item, i) => {
					callItemCallback(($item) => {
						const selectableContainer = getSelectableContainerVelo($item)
						selectableContainer.inputType = getInputType()
					})(item, i)
				})
			},
			onItemReady(cb: OnItemCallback) {
				if (!validateFunctionTypeAndReport(cb, 'callback', 'onItemReady')) {
					return
				}

				setItemReadyCallbacks(cb, 'onItemReady')
			},
			onItemRemoved(cb: OnItemRemovedCallback) {
				if (!validateFunctionTypeAndReport(cb, 'callback', 'onItemRemoved')) {
					return
				}

				repeaterState.onItemRemovedCallbacks.push(callCallbackWithLog('onItemRemoved', cb))
			},
			onItemSelected(cb: OnItemCallback) {
				if (!validateSelectableRepeaterAndReport('onItemSelected') || !validateFunctionTypeAndReport(cb, 'callback', 'onItemSelect')) {
					return
				}

				const onItemSelectedCallback = callItemCallback(cb)

				repeaterState.onItemSelectedCallbacks.push(callCallbackWithLog('onItemSelected', onItemSelectedCallback))
			},
			forEachItem(cb) {
				if (!validateFunctionTypeAndReport(cb, 'callback', 'forEachItem')) {
					return
				}

				repeaterState.currentItems.forEach((item, index) => callItemCallback(cb)(item, index))
			},
			forItems(requestedItemIds, cb) {
				if (!Array.isArray(requestedItemIds)) {
					reportWrongType('itemIds', 'forItems', requestedItemIds, 'array')
					return
				}
				if (!validateFunctionTypeAndReport(cb, 'callback', 'forItems')) {
					return
				}

				const repeaterItemsIds = new Set(repeaterState.currentItems.map((item) => item._id))
				const existingItemIds = new Set()

				requestedItemIds.forEach((itemId) => {
					if (repeaterItemsIds.has(itemId)) {
						existingItemIds.add(itemId)
					} else {
						reporter.logSdkWarning(`The data that was passed to forItems contained non existing item with the ID: ${itemId}.`)
					}
				})

				repeaterState.currentItems.forEach((item, i) => {
					if (existingItemIds.has(item._id)) {
						callItemCallback(cb)(item, i)
					}
				})
			},
			clearSelection() {
				if (!validateSelectableRepeaterAndReport('clearSelection')) {
					return
				}

				setSelectedIndices([])
			},
		}
	}

	const accessibilityPropsSDK = createAccessibilityPropSDKFactory({
		enableAriaLabel: false,
		enableAriaLabelledBy: true,
		enableAriaDescribedBy: true,
		enableAriaControls: false,
		enableAriaOwns: false,
		enableAriaErrorMessage: false,
		enableTabIndex: false,
	})
	// @ts-ignore TODO: fix types
	return composeSDKFactories([sdk, createElementPropsSDKFactory(), accessibilityPropsSDK])
}
